import {
    NETWORKS_SPECIAL_DISPLAY_NAME_MAPPING,
    NETWORKS_SPECIAL_ORIGINAL_DISPLAY_NAME_MAPPING,
} from '../utils/adNetworks';
import { ReportFieldTypes } from '../utils/consts';

// --------------------
// Constant Mappings
// --------------------

const GRID_FILTER_LOGICAL_OPERATORS = {
    AND: 'AND',
    OR: 'OR',
};

const GRID_FILTER_OPERATORS = {
    contains: 'contains',
    notContains: 'notContains',
    equals: 'equals',
    notEqual: 'notEqual',
    startsWith: 'startsWith',
    endsWith: 'endsWith',
    lessThan: 'lessThan',
    lessThanOrEqual: 'lessThanOrEqual',
    greaterThan: 'greaterThan',
    greaterThanOrEqual: 'greaterThanOrEqual',
    inRange: 'inRange',
    ...GRID_FILTER_LOGICAL_OPERATORS,
};

const POST_PROCESS_OPERATOR_TO_API = {
    [GRID_FILTER_OPERATORS.lessThan]: 'lt',
    [GRID_FILTER_OPERATORS.lessThanOrEqual]: 'lte',
    [GRID_FILTER_OPERATORS.greaterThan]: 'gt',
    [GRID_FILTER_OPERATORS.greaterThanOrEqual]: 'gte',
    [GRID_FILTER_OPERATORS.notEqual]: 'not equals',
    [GRID_FILTER_OPERATORS.equals]: 'equals',
    [GRID_FILTER_OPERATORS.inRange]: 'inRange',
    [GRID_FILTER_OPERATORS.AND]: 'and',
    [GRID_FILTER_OPERATORS.OR]: 'or',
    [GRID_FILTER_OPERATORS.notContains]: 'not contains',
    [GRID_FILTER_OPERATORS.contains]: 'contains',
    [GRID_FILTER_OPERATORS.startsWith]: 'startswith',
    [GRID_FILTER_OPERATORS.endsWith]: 'endswith',
};

const QUERY_FILTER_OPERATOR_TO_FUNCTION = {
    [GRID_FILTER_OPERATORS.contains]: (displayName, textValue) => displayName.includes(textValue),
    [GRID_FILTER_OPERATORS.notContains]: (displayName, textValue) => !displayName.includes(textValue),
    [GRID_FILTER_OPERATORS.equals]: (displayName, textValue) => displayName === textValue,
    [GRID_FILTER_OPERATORS.notEqual]: (displayName, textValue) => displayName !== textValue,
    [GRID_FILTER_OPERATORS.startsWith]: (displayName, textValue) => displayName.startsWith(textValue),
    [GRID_FILTER_OPERATORS.endsWith]: (displayName, textValue) => displayName.endsWith(textValue),
};

const LOGICAL_OPERATOR_TO_FUNCTION = {
    [GRID_FILTER_OPERATORS.AND]: (optionName, conditions) => conditions.every(condition => condition(optionName)),
    [GRID_FILTER_OPERATORS.OR]: (optionName, conditions) => conditions.some(condition => condition(optionName)),
};

// --------------------
// Generic Filter Builders
// --------------------

const extrapolateText = (value, def) => {
    if (def.type === 'dimension') {
        const lowerValue = value?.toLowerCase().trim();
        return lowerValue !== 'n/a' || value === 'country_field' ? value : null;
    }
    const numberValue = Number(value);
    return def.context?.displayFormat?.type === 'percentage' ? numberValue / 100 : numberValue;
};

// --------------------
// Post Process Filter Builders
// --------------------

const extrapolateOperator = (filter, def) => {
    const operator = POST_PROCESS_OPERATOR_TO_API[filter.type] || filter.type;
    return operator === GRID_FILTER_OPERATORS.contains && def.type === ReportFieldTypes.METRIC
        ? GRID_FILTER_OPERATORS.equals
        : operator;
};

const buildBasicFilter = (filter, def) => {
    const value = extrapolateText(filter.filter, def);
    const operator = value !== null ? extrapolateOperator(filter, def) : GRID_FILTER_OPERATORS.equals;
    const valueTo = operator === GRID_FILTER_OPERATORS.inRange ? extrapolateText(filter.filterTo, def) : null;

    return {
        field: filter.field,
        value,
        valueTo,
        operator,
    };
};

const buildComplexFilter = (filter, def) => ({
    field: filter.field,
    operator: POST_PROCESS_OPERATOR_TO_API[filter.operator],
    value: filter.conditions.map(condition => buildBasicFilter({ ...condition, field: filter.field }, def)),
});

export const getPostProcessFilter = (filter, def) =>
    GRID_FILTER_LOGICAL_OPERATORS[filter.operator] ? buildComplexFilter(filter, def) : buildBasicFilter(filter, def);

// --------------------
// Query Filter Builders
// --------------------

const getBasicFilterFunction = (filter, def) => {
    const extractedValue = extrapolateText(filter.filter, def);
    const textValue = extractedValue !== null ? extractedValue.toLowerCase() : null;
    const operation =
        QUERY_FILTER_OPERATOR_TO_FUNCTION[filter.type || GRID_FILTER_OPERATORS.contains] ||
        QUERY_FILTER_OPERATOR_TO_FUNCTION.contains;
    return optionName => {
        if (textValue === null) return false;
        return operation(optionName.toLowerCase(), textValue);
    };
};

export const getValuesForQueryFilter = (filter, dimensionValuesMap, def) => {
    const isLogicalOperatorFilter = !!LOGICAL_OPERATOR_TO_FUNCTION[filter.operator];

    if (!isLogicalOperatorFilter && extrapolateText(filter.filter, def) === null) return [null];

    const conditions = isLogicalOperatorFilter
        ? filter.conditions.map(condition => getBasicFilterFunction(condition, def))
        : [getBasicFilterFunction(filter, def)];

    return dimensionValuesMap[filter.field]
        .filter(({ name, display_name: displayName }) => {
            const optionName =
                NETWORKS_SPECIAL_DISPLAY_NAME_MAPPING[name] === displayName
                    ? NETWORKS_SPECIAL_ORIGINAL_DISPLAY_NAME_MAPPING[name]
                    : displayName;

            const operation = LOGICAL_OPERATOR_TO_FUNCTION[filter.operator] || LOGICAL_OPERATOR_TO_FUNCTION.OR;
            return operation(optionName, conditions);
        })
        .map(({ name }) => name);
};
